import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { useLocalStorageState } from '@components/component-helpers';
import { DesignAvatar } from '@components/design-avatar';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { ExpandableSearch } from '@components/expandable-search';
import { useDeleteModal } from '@components/generic-modals';
import { Header } from '@components/header';
import { IconContainer } from '@components/icon-container';
import { IconAI } from '@components/icons/ai';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconSortAsc } from '@components/icons/sort-asc';
import { IconSortDesc } from '@components/icons/sort-desc';
import { IconTrash } from '@components/icons/trash';
import { ListCard } from '@components/list-card';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { Separator } from '@components/separator';
import { useSidebarState } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { toQueryString } from '@utils/requests';

import { useAIAgents, useAIAgentTemplateUpdates, useRemoveAIAgent } from 'api/ai-agents/hooks';
import { AIAgent, VectorStoreStatus } from 'api/ai-agents/requests';

import { useAgentDetailsSidebar } from './agent-details-sidebar';
import { getModelLabel, getModelProviderName, PROVIDER_ICONS } from './ai-agent-config';
import { useDuplicateAIAgentTemplateModal } from './duplicate-modal';

export const getAgentIcon = (aiAgent: AIAgent, iconSize: number = 40) => {
  return aiAgent.picture ? (
    <DesignAvatar src={aiAgent.picture} size={iconSize} />
  ) : (
    <IconAI size={iconSize} />
  );
};

const orderingItems = [
  'name',
  '-name',
  '-date_created',
  'date_created',
  '-date_updated',
  'date_updated',
] as const;

export const AIAgentList = () => {
  const { t } = useScopedTranslate(locale);
  const [searchStr, setSearchStr] = useState('');
  const [ordering, setOrdering] = useLocalStorageState<(typeof orderingItems)[number]>({
    key: 'ai-agent-list-order',
    defaultValue: '-date_created',
  });
  const queryString = useMemo(() => {
    return toQueryString({
      ...(searchStr && { search: searchStr }),
      ordering,
    });
  }, [searchStr, ordering]);
  const { data: aiAgents, isLoading: isAIAgentsLoading } = useAIAgents({ queryString });
  useAIAgentTemplateUpdates();
  const isDataEmpty = !aiAgents || aiAgents.length === 0;
  const isDescendingOrdering = ordering.startsWith('-');
  const showAgentDetailsSidebar = useAgentDetailsSidebar();
  const { activeId } = useSidebarState();
  const showDeleteModal = useDeleteModal();
  const showDuplicateModal = useDuplicateAIAgentTemplateModal();
  const removeAIAgentMutation = useRemoveAIAgent();

  if (isAIAgentsLoading) return <Spinner />;

  return (
    <>
      <Header
        actions={
          <Stack orientation="horizontal" space={2}>
            <ExpandableSearch onChange={setSearchStr} />
            <Menu
              maxWidth="auto"
              width="auto"
              buttonProps={{
                variant: 'secondary',
                text: t(`sort_${ordering}`).split(' • ')[0],
                iconLeft: isDescendingOrdering ? <IconSortDesc /> : <IconSortAsc />,
              }}
            >
              <Menu.Label label={t('sort')} />
              {orderingItems.map((item) => (
                <Menu.Item
                  key={item}
                  onSelect={() => setOrdering(item)}
                  isActive={ordering === item}
                  label={t(`sort_${item}`)}
                />
              ))}
            </Menu>
            <Button
              data-cy="new-journey-btn"
              text={t('createAgent')}
              iconLeft={<IconAiAgent />}
              onClick={() => showAgentDetailsSidebar()}
            />
          </Stack>
        }
        breadcrumbs={
          <Stack orientation="horizontal" space={2} align="center">
            <IconAiAgent variant="gradient" />
            <Breadcrumb items={[{ label: t('aiAgentTemplates') }]} />
          </Stack>
        }
      />
      <PageContent>
        {isDataEmpty ? (
          searchStr ? (
            <EmptySearchState skipMargin />
          ) : (
            <EmptyState
              skipMargin
              header={t('emptyStateHeader')}
              content={t('emptyStateContent')}
              icon={<IconAiAgent size={80} variant="gradient" />}
              iconShadow={<IconAiAgent size={240} variant="gradient" style={{ opacity: 0.1 }} />}
            />
          )
        ) : (
          <Stack space={6}>
            <BannerContainer>
              <EmptyState
                skipMargin
                icon={<IconAiAgent size={40} variant="gradient" />}
                iconShadow={<IconAiAgent size={120} variant="gradient" style={{ opacity: 0.1 }} />}
                variant="small"
                header={t('bannerHeader')}
                headerColor="indigo200"
                contentColor="indigo100"
                content={t('bannerContent')}
              />
            </BannerContainer>
            <JourneyItemContainer space={2}>
              {aiAgents?.map((aiAgent) => {
                const hasFiles = aiAgent.files && aiAgent.files.length > 0;
                const vectorStoreStatusMap: Record<
                  VectorStoreStatus,
                  {
                    variant: 'green' | 'orange' | 'red' | 'neutral';
                    textKey: keyof typeof locale.en;
                  }
                > = {
                  ready: { variant: 'green', textKey: 'statusReady' },
                  indexing: { variant: 'orange', textKey: 'statusIndexing' },
                  failed: { variant: 'red', textKey: 'statusFailed' },
                  pending: { variant: 'neutral', textKey: 'statusPending' },
                };
                const vectorStoreConfig =
                  hasFiles && aiAgent.vectorStoreStatus
                    ? vectorStoreStatusMap[aiAgent.vectorStoreStatus]
                    : null;
                const ProviderIcon = PROVIDER_ICONS[getModelProviderName(aiAgent.model)];

                return (
                  <ListCard
                    key={aiAgent.shortId}
                    title={aiAgent.name}
                    description={aiAgent.description}
                    isActive={activeId === aiAgent.shortId}
                    icon={getAgentIcon(aiAgent)}
                    onClick={() => showAgentDetailsSidebar(aiAgent, { id: aiAgent.shortId })}
                    actions={
                      <Stack orientation="horizontal" space={4} align="center">
                        {vectorStoreConfig && (
                          <Badge
                            size="md"
                            variant={vectorStoreConfig.variant}
                            text={t(vectorStoreConfig.textKey)}
                          />
                        )}
                        <IconContainer
                          size="small"
                          icon={<ProviderIcon size={16} color="iconNeutral" />}
                        />
                        <Separator orientation="horizontal" />
                        <Stack align="center" style={{ minWidth: pxToRem(80) }}>
                          <Text font="bold14" text={t('model')} />
                          <Text
                            font="regular12"
                            text={getModelLabel(aiAgent.model)}
                            color="textSecondary"
                          />
                        </Stack>
                        <Separator orientation="horizontal" />
                        <Menu buttonProps={{ variant: 'ghost', iconRight: <IconMoreVertical /> }}>
                          <Menu.Item
                            label={t('core.edit')}
                            icon={<IconEdit />}
                            onSelect={() =>
                              showAgentDetailsSidebar(aiAgent, { id: aiAgent.shortId })
                            }
                          />
                          <Menu.Item
                            label={t('core.duplicate')}
                            icon={<IconDuplicate />}
                            onSelect={() => showDuplicateModal({ aiAgent })}
                          />
                          <Menu.Item
                            label={t('core.delete')}
                            icon={<IconTrash />}
                            variant="danger"
                            onSelect={() =>
                              showDeleteModal({
                                title: t('deleteTitle'),
                                description: styledTranslate({
                                  key: 'deleteDescription',
                                  variables: {
                                    note: {
                                      text: t('deleteNote'),
                                      font: 'bold16',
                                    },
                                  },
                                  translations: locale,
                                }),
                                hasUserConfirmation: true,
                                buttonText: t('core.delete'),
                                buttonIcon: <IconTrash />,
                                onConfirm: () => removeAIAgentMutation.mutate(aiAgent.shortId),
                              })
                            }
                          />
                        </Menu>
                      </Stack>
                    }
                  />
                );
              })}
            </JourneyItemContainer>
          </Stack>
        )}
      </PageContent>
    </>
  );
};

const BannerContainer = styled.div({
  backgroundColor: theme.color.indigoTrans5,
  borderRadius: pxToRem(8),
});

const JourneyItemContainer = styled(Stack)({
  flexGrow: 1,
  position: 'relative',
  display: 'flex',
  '@media(max-width: 768px)': {
    overflowY: 'visible',
  },
});

const locale = {
  en: {
    aiAgentTemplates: 'AI Agent templates',
    createAgent: 'Create Agent template',
    emptyStateContent:
      'Create and manage AI agents templates for your company. These agents can be duplicated into journeys and used to empower and enhance your journey workflows',
    emptyStateHeader: 'You do not have any AI Agent templates',
    bannerHeader: 'AI Agent templates',
    bannerContent:
      'Create and manage AI agents templates for your company.\nThese agents can be duplicated into journeys and used to empower and enhance your journey workflows',
    deleteTitle: 'Delete AI Agent',
    deleteDescription:
      'You are about to permanently delete this AI Agent template. Once deleted, it will no longer be available for selection in the journey builder for creating new agent actions.\n\n{note}: Existing agent actions created from this template will continue to function normally.',
    deleteNote: 'Note',
    statusReady: 'Active',
    statusIndexing: 'Indexing...',
    statusFailed: 'Indexing Failed',
    statusPending: 'Pending',
    model: 'Model',
    sort: 'Sort by',
    sort_name: 'Alphabetical ascending • A → Z',
    'sort_-name': 'Alphabetical descending • Z → A',
    'sort_-date_created': 'Created • Newest',
    sort_date_created: 'Created • Oldest',
    'sort_-date_updated': 'Last edit • New → Old',
    sort_date_updated: 'Last edit • Old → New',
  },
};
